/**
 * Shared detailed module review content for dialog and full-page layouts.
 *
 * @module ModuleAnswerReviewContent
 */
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2,
  ShieldOff,
  Mic,
  Languages,
  AlignLeft,
  Type,
  Book,
  Waves,
  MessageSquare,
  Feather,
  MessageCircle,
  ListChecks,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  SIMULATION_MODULE_ICONS,
  SIMULATION_MODULE_SHORT_LABELS,
} from "@/lib/simulationModuleDisplay";
import { isAudioDeletedAfterRetention } from "@/lib/consentConfig";
import { AnnotatedAnswer } from "./AnnotatedWritingAnswer";
import { AnswerComparisonTabs } from "./AnswerComparisonTabs";
import { CorrectedAnswerPanel } from "./CorrectedAnswerPanel";
import { RubricScorecard, type ScorecardCategory } from "./RubricScorecard";
import {
  ResultAudioPlayer,
  StudentAnswerAudioPlayer,
} from "./StudentAnswerAudioPlayer";
import { TranscriptWithTimestamps } from "./TranscriptWithTimestamps";
import {
  buildAnswerAnnotationModel,
  buildAnswerGroups,
  buildQuestionNavigatorItems,
  buildSectionReviewBlocks,
  getBlankAnnotationId,
  getPronunciationAnnotationId,
  getQuestionScrollTop,
  getTextAnnotationId,
  hasSubmittedAnswer,
  normalizeAnswerText,
  sectionFeedbackRowHasContent,
  type AnswerAnnotationModel,
  type AnswerTextAnnotationSegment,
} from "./moduleAnswerReviewModel";
import type { ModuleResult } from "@/types/simulation-results";
import type { ContextGroup, ModuleAnswer } from "@/types/simulation-results";
import { ItemRatingReadOnly } from "@/components/rating/ItemRatingReadOnly";
import { RatingRow } from "@/components/rating/RatingRow";
import { useItemRatings } from "@/hooks/useItemRatings";
import { useSubmissionItemRatings } from "@/hooks/useSubmissionItemRatings";
import type { RatingModule } from "@/lib/ratingConstants";
import { SectionFeedbackCard } from "@/components/results/SectionFeedbackPanel";
import { useSectionFeedback } from "@/hooks/useSectionFeedback";
import { isModuleReviewAvailable } from "@/lib/analysisStatus";
import { getAnalysisSectionTheme } from "@/lib/analysisSectionThemes";
import {
  getCriterionDisplayFeedback,
  getCriterionMeta,
  getCorrectedVersion,
  hasWeightedRubric,
  getFeedbackSummary,
  getFeedbackText,
  getWordTimestamps,
  normalizeAnalysisBreakdown,
} from "@/lib/analysisBreakdown";

export interface ModuleAnswerReviewContentProps {
  readonly sessionId?: string;
  readonly moduleName: string;
  readonly answers: ModuleAnswer[];
  readonly isLoading?: boolean;
  readonly sessionModules?: ModuleResult[];
  readonly onSessionModuleChange?: (module: string) => void;
  readonly layout?: "dialog" | "page";
  readonly className?: string;
  readonly headerAction?: React.ReactNode;
  /** When `learner`, show submission-owner ratings read-only (admin preview). */
  readonly itemRatingsMode?: "interactive" | "learner";
}

const parseExpectedAnswers = (expectedAnswer?: string): string[] => {
  if (!expectedAnswer) return [];
  try {
    const parsed = JSON.parse(expectedAnswer);
    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    if (typeof parsed === "string") {
      return parsed ? [parsed] : [];
    }
  } catch {
    // treat as plain string
  }
  return expectedAnswer ? [expectedAnswer] : [];
};

type ReviewFeedbackCategory =
  | "pronunciation"
  | "fluency"
  | "grammar"
  | "spelling"
  | "structure"
  | "task_fulfillment"
  | "strength"
  | "weakness"
  | "note";

interface FeedbackAnnotationReference {
  readonly id: string;
  readonly label: string;
}

interface FeedbackCorrectionPair {
  readonly wrong: string;
  readonly correction: string;
}

type ReviewTranslate = (
  key: string,
  options?: Record<string, string | number | undefined>,
) => string;

const normalizeFeedbackLookup = (value?: string | null): string =>
  normalizeAnswerText(value).normalize("NFC").toLowerCase();

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const containsStandaloneTerm = (
  feedbackText: string,
  term?: string | null,
): boolean => {
  const normalizedTerm = normalizeFeedbackLookup(term);
  if (normalizedTerm.length < 3) return false;

  if (/\s/.test(normalizedTerm)) {
    return feedbackText.includes(normalizedTerm);
  }

  return new RegExp(
    `(^|[^\\p{L}\\p{N}])${escapeRegExp(normalizedTerm)}($|[^\\p{L}\\p{N}])`,
    "u",
  ).test(feedbackText);
};

const containsAnyTerm = (
  feedbackText: string,
  term?: string | null,
): boolean => {
  const normalizedTerm = normalizeFeedbackLookup(term);
  if (!normalizedTerm) return false;

  if (/\s/.test(normalizedTerm)) {
    return feedbackText.includes(normalizedTerm);
  }

  return new RegExp(
    `(^|[^\\p{L}\\p{N}])${escapeRegExp(normalizedTerm)}($|[^\\p{L}\\p{N}])`,
    "u",
  ).test(feedbackText);
};

const containsFeedbackTerm = (
  feedbackText: string,
  term?: string | null,
): boolean => {
  const normalizedTerm = normalizeFeedbackLookup(term);
  return (
    normalizedTerm.length >= 3 &&
    containsStandaloneTerm(feedbackText, normalizedTerm)
  );
};

const containsCorrectionPair = (
  feedbackText: string,
  wrong?: string | null,
  correction?: string | null,
): boolean => {
  const normalizedWrong = normalizeFeedbackLookup(wrong);
  const normalizedCorrection = normalizeFeedbackLookup(correction);
  if (!normalizedWrong || !normalizedCorrection) return false;

  const arrowPattern = /(?:->|→)/g;
  let arrowMatch = arrowPattern.exec(feedbackText);

  while (arrowMatch) {
    const left = feedbackText.slice(
      Math.max(0, arrowMatch.index - 120),
      arrowMatch.index,
    );
    const right = feedbackText.slice(
      arrowMatch.index + arrowMatch[0].length,
      arrowMatch.index + arrowMatch[0].length + 120,
    );

    if (
      containsAnyTerm(left, normalizedWrong) &&
      containsAnyTerm(right, normalizedCorrection)
    ) {
      return true;
    }

    arrowMatch = arrowPattern.exec(feedbackText);
  }

  const insteadPattern = /(instead of|statt|anstelle von|rather than)/g;
  let insteadMatch = insteadPattern.exec(feedbackText);

  while (insteadMatch) {
    const left = feedbackText.slice(
      Math.max(0, insteadMatch.index - 120),
      insteadMatch.index,
    );
    const right = feedbackText.slice(
      insteadMatch.index + insteadMatch[0].length,
      insteadMatch.index + insteadMatch[0].length + 120,
    );

    if (
      containsAnyTerm(left, normalizedCorrection) &&
      containsAnyTerm(right, normalizedWrong)
    ) {
      return true;
    }

    insteadMatch = insteadPattern.exec(feedbackText);
  }

  return false;
};

const extractReferencedBlankIndices = (feedbackText: string): Set<number> => {
  const indices = new Set<number>();
  const pattern = /(?:blank|lücke|luecke|feld)\s*#?\s*(\d+)/gi;
  let match = pattern.exec(feedbackText);

  while (match) {
    indices.add(Number(match[1]));
    match = pattern.exec(feedbackText);
  }

  return indices;
};

const formatTextReferenceLabel = (
  segment: AnswerTextAnnotationSegment,
): string =>
  segment.correction ? `${segment.text} → ${segment.correction}` : segment.text;

const textFieldFromRecord = (value: unknown, ...keys: string[]): string => {
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const raw = record[key];
    const text = normalizeAnswerText(
      raw === null || raw === undefined ? null : String(raw),
    );
    if (text) return text;
  }
  return "";
};

const parseArrowCorrectionPair = (
  value: string,
): FeedbackCorrectionPair | null => {
  const match = value.match(/(.+?)(?:->|→)(.+)/);
  if (!match) return null;
  const wrong = normalizeAnswerText(match[1]);
  const correction = normalizeAnswerText(match[2]);
  return wrong && correction ? { wrong, correction } : null;
};

const getSpellingCorrectionPairs = (
  answer: ModuleAnswer,
): FeedbackCorrectionPair[] => {
  const typos = normalizeAnalysisBreakdown(answer)?.feedback?.spelling?.typos;
  if (!Array.isArray(typos)) return [];

  return typos.flatMap((entry): FeedbackCorrectionPair[] => {
    if (typeof entry === "string") {
      const parsed = parseArrowCorrectionPair(entry);
      return parsed ? [parsed] : [];
    }
    if (!entry || typeof entry !== "object") return [];

    const wrong = textFieldFromRecord(
      entry,
      "word",
      "wrong",
      "example",
      "error",
    );
    const correction = textFieldFromRecord(
      entry,
      "correction",
      "correct",
      "expected",
    );
    return wrong && correction ? [{ wrong, correction }] : [];
  });
};

const getFluencyIssueTexts = (answer: ModuleAnswer): string[] => {
  const issues = normalizeAnalysisBreakdown(answer)?.feedback?.fluency?.issues;
  if (!Array.isArray(issues)) return [];

  return issues
    .map((entry) => {
      if (typeof entry === "string") {
        return normalizeAnswerText(entry);
      }
      return textFieldFromRecord(
        entry,
        "word",
        "phrase",
        "issue",
        "text",
        "note",
        "example",
      );
    })
    .filter(Boolean);
};

const correctionPairMatchesSegment = (
  pair: FeedbackCorrectionPair,
  segment: AnswerTextAnnotationSegment,
): boolean =>
  normalizeFeedbackLookup(pair.wrong) ===
    normalizeFeedbackLookup(segment.text) &&
  normalizeFeedbackLookup(pair.correction) ===
    normalizeFeedbackLookup(segment.correction);

const canUseCorrectionPairReferences = (
  category: ReviewFeedbackCategory,
): boolean => category !== "pronunciation" && category !== "strength";

const canUseStandaloneTextReferences = (
  category: ReviewFeedbackCategory,
): boolean =>
  category === "grammar" ||
  category === "fluency" ||
  category === "weakness" ||
  category === "note";

const buildFeedbackAnnotationReferences = ({
  answer,
  model,
  feedbackText,
  category,
  t,
  spellingCorrectionPairs: rawSpellingCorrectionPairs,
  excludedReferenceIds,
}: {
  readonly answer: ModuleAnswer;
  readonly model: AnswerAnnotationModel | null;
  readonly feedbackText: string;
  readonly category: ReviewFeedbackCategory;
  readonly t: ReviewTranslate;
  readonly spellingCorrectionPairs?: FeedbackCorrectionPair[];
  readonly excludedReferenceIds?: ReadonlySet<string>;
}): FeedbackAnnotationReference[] => {
  const spellingCorrectionPairs = rawSpellingCorrectionPairs ?? [];
  const canReferenceSpellingPairs =
    category === "spelling" && spellingCorrectionPairs.length > 0;
  if (!feedbackText.trim() && !canReferenceSpellingPairs) return [];

  const feedbackLookup = normalizeFeedbackLookup(feedbackText);
  const references = new Map<string, FeedbackAnnotationReference>();
  const addReference = (id: string, label: string) => {
    if (excludedReferenceIds?.has(id)) return;
    if (!references.has(id)) {
      references.set(id, { id, label });
    }
  };

  if (model?.kind === "fill-in-blank") {
    const referencedBlankIndices = extractReferencedBlankIndices(feedbackText);

    model.blanks.forEach((blank) => {
      const isExplicitlyReferenced = referencedBlankIndices.has(blank.index);

      if (!isExplicitlyReferenced) return;

      addReference(
        getBlankAnnotationId(answer.id, blank.index),
        t("simulation.results.reviewDialog.blankLabel", {
          number: blank.index,
          defaultValue: `Blank ${blank.index}`,
        }),
      );
    });
  }

  if (model?.kind === "text") {
    const issueSegments = model.segments.filter(
      (segment) => segment.state === "issue",
    );
    const matchedSegments = issueSegments.filter(
      (segment) =>
        (category === "spelling" &&
          spellingCorrectionPairs.some((pair) =>
            correctionPairMatchesSegment(pair, segment),
          )) ||
        (canUseCorrectionPairReferences(category) &&
          containsCorrectionPair(
            feedbackLookup,
            segment.text,
            segment.correction,
          )) ||
        (canUseStandaloneTextReferences(category) &&
          (containsFeedbackTerm(feedbackLookup, segment.text) ||
            containsFeedbackTerm(feedbackLookup, segment.correction))),
    );

    matchedSegments.forEach((segment) => {
      if (!segment.annotationIndex) return;
      addReference(
        getTextAnnotationId(answer.id, segment.annotationIndex),
        formatTextReferenceLabel(segment),
      );
    });
  }

  const timestampReferences = getWordTimestamps(answer)
    .map((word, idx) => ({ word, idx }))
    .filter(({ word }) => category === "fluency" || Boolean(word.issue));

  const matchedTimestampReferences = timestampReferences.filter(
    ({ word }) =>
      containsFeedbackTerm(feedbackLookup, word.word) ||
      containsFeedbackTerm(feedbackLookup, word.issue) ||
      containsFeedbackTerm(feedbackLookup, word.tip),
  );

  const timestampReferencesToUse =
    category === "pronunciation"
      ? matchedTimestampReferences.length === 0
        ? timestampReferences
        : matchedTimestampReferences
      : category === "fluency"
        ? matchedTimestampReferences
        : [];

  timestampReferencesToUse.forEach(({ word, idx }) => {
    addReference(getPronunciationAnnotationId(answer.id, idx + 1), word.word);
  });

  return Array.from(references.values());
};

interface CriterionFeedbackSectionConfig {
  readonly key: string;
  readonly text: string;
  readonly labelKey: string;
  readonly defaultLabel: string;
  readonly labelClassName: string;
  readonly boxClassName: string;
  readonly icon?: React.ReactNode;
  readonly referenceCategory?: ReviewFeedbackCategory;
}

const resolveSectionReferences = (
  category: ReviewFeedbackCategory | undefined,
  pronunciationReferences: FeedbackAnnotationReference[],
  fluencyReferences: FeedbackAnnotationReference[],
  grammarReferences: FeedbackAnnotationReference[],
  spellingReferences: FeedbackAnnotationReference[],
  structureReferences: FeedbackAnnotationReference[],
  taskFulfillmentReferences: FeedbackAnnotationReference[],
): FeedbackAnnotationReference[] => {
  if (category === "pronunciation") return pronunciationReferences;
  if (category === "fluency") return fluencyReferences;
  if (category === "grammar") return grammarReferences;
  if (category === "spelling") return spellingReferences;
  if (category === "structure") return structureReferences;
  if (category === "task_fulfillment") return taskFulfillmentReferences;
  return [];
};

export function ModuleAnswerReviewContent({
  sessionId,
  moduleName,
  answers,
  isLoading = false,
  sessionModules = [],
  onSessionModuleChange,
  layout = "dialog",
  className,
  headerAction,
  itemRatingsMode = "interactive",
}: ModuleAnswerReviewContentProps) {
  const { t } = useTranslation();
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const audioRefsMap = useRef<Map<string, React.RefObject<HTMLAudioElement>>>(
    new Map(),
  );
  const annotationHighlightTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(
    null,
  );

  useEffect(
    () => () => {
      if (annotationHighlightTimerRef.current) {
        clearTimeout(annotationHighlightTimerRef.current);
      }
    },
    [],
  );

  const getAudioRef = (answerId: string): React.RefObject<HTMLAudioElement> => {
    if (!audioRefsMap.current.has(answerId)) {
      audioRefsMap.current.set(answerId, { current: null });
    }
    return audioRefsMap.current.get(answerId)!;
  };

  const handleAnnotationReferenceClick = (annotationId: string) => {
    setActiveAnnotationId(annotationId);

    if (annotationHighlightTimerRef.current) {
      clearTimeout(annotationHighlightTimerRef.current);
    }

    if (typeof document !== "undefined") {
      document.getElementById(annotationId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    annotationHighlightTimerRef.current = setTimeout(() => {
      setActiveAnnotationId((current) =>
        current === annotationId ? null : current,
      );
    }, 3500);
  };

  const renderFeedbackReferences = (
    references: readonly FeedbackAnnotationReference[],
  ) => {
    if (references.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border/50 pt-2">
        <span className="font-ui text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t("simulation.results.reviewDialog.references", {
            defaultValue: "References",
          })}
        </span>
        {references.map((reference) => (
          <button
            key={reference.id}
            type="button"
            onClick={() => handleAnnotationReferenceClick(reference.id)}
            aria-controls={reference.id}
            aria-label={t("simulation.results.reviewDialog.jumpToReference", {
              label: reference.label,
              defaultValue: `Jump to ${reference.label}`,
            })}
            className="font-ui inline-flex rounded-md border border-primary/20 bg-background px-2 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            data-feedback-reference={reference.id}
          >
            {reference.label}
          </button>
        ))}
      </div>
    );
  };
  const isPageLayout = layout === "page";

  const moduleReviewLabel = t(`moduleSelection.modules.${moduleName}.label`, {
    defaultValue:
      SIMULATION_MODULE_SHORT_LABELS[
        moduleName as keyof typeof SIMULATION_MODULE_SHORT_LABELS
      ] ?? moduleName,
  });
  const reviewRegionAriaLabel = t(
    "simulation.results.reviewDialog.regionAria",
    {
      module: moduleReviewLabel,
      defaultValue: `${moduleReviewLabel} answer review`,
    },
  );

  const gradingPendingLabel = t("simulation.results.status.analysing", {
    defaultValue: "Analysing...",
  });
  const correctLabel = t("simulation.results.reviewDialog.correct", {
    defaultValue: "Correct",
  });
  const incorrectLabel = t("simulation.results.reviewDialog.incorrect", {
    defaultValue: "Incorrect",
  });
  const notAnsweredLabel = t("simulation.results.reviewDialog.notAnswered", {
    defaultValue: "Not answered",
  });

  const formatQuestionTypeLabel = (questionType?: string | null): string => {
    const fallback = questionType
      ? questionType
          .split("-")
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      : "-";
    return t(`simulation.results.reviewDialog.questionTypes.${questionType}`, {
      defaultValue: fallback,
    });
  };

  const scrollToQuestion = (answerId: string) => {
    const el = questionRefs.current.get(answerId);
    if (!el) return;
    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const viewportRect = viewport.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = getQuestionScrollTop(
      viewportRect.top,
      viewport.scrollTop,
      elRect.top,
    );
    viewport.scrollTo({ top: offset, behavior: "smooth" });
  };

  const groupedAnswers = useMemo(() => buildAnswerGroups(answers), [answers]);
  const questionNavigatorItems = useMemo(
    () => buildQuestionNavigatorItems(groupedAnswers.displayAnswers),
    [groupedAnswers.displayAnswers],
  );
  // 1..N number per answer in the same (exam) order as the navigator chips,
  // so each question card can show a matching minimal "Q#" tag.
  const questionNumberById = useMemo(() => {
    const map = new Map<string, number>();
    questionNavigatorItems.forEach((item) =>
      map.set(item.answer.id, item.number),
    );
    return map;
  }, [questionNavigatorItems]);

  const answerIds = useMemo(
    () => answers.map((answer) => answer.id),
    [answers],
  );
  const ratingModule = moduleName.toLowerCase() as RatingModule;
  const isLearnerRatingsMode = itemRatingsMode === "learner";
  const { byKey: interactiveRatingsByKey } = useItemRatings(
    answerIds,
    !isLearnerRatingsMode,
  );
  const { byKey: learnerRatingsByKey } = useSubmissionItemRatings(
    sessionId,
    moduleName,
    answerIds,
    isLearnerRatingsMode,
  );
  const ratingsByKey = isLearnerRatingsMode
    ? learnerRatingsByKey
    : interactiveRatingsByKey;

  const activeModuleResult = sessionModules.find(
    (mod) => mod.module.toLowerCase() === moduleName.toLowerCase(),
  );

  // Per-section LLM feedback (strengths / weaknesses / notes).
  const { feedback: sectionFeedback } = useSectionFeedback(
    sessionId,
    moduleName,
  );

  const moduleLower = moduleName.toLowerCase();
  const useInterleavedSectionReview =
    moduleLower === "reading" ||
    moduleLower === "listening" ||
    moduleLower === "writing" ||
    moduleLower === "speaking";

  const sectionReviewBlocks = useMemo(() => {
    if (!useInterleavedSectionReview) {
      return [];
    }
    return buildSectionReviewBlocks(groupedAnswers, sectionFeedback);
  }, [groupedAnswers, sectionFeedback, useInterleavedSectionReview]);

  const expectedAnswerCount = answers.filter(
    (answer) => answer.is_correct === true,
  ).length;
  const hasCorrectnessGrade = answers.some(
    (answer) => typeof answer.is_correct === "boolean",
  );

  const formatScoreValue = (value: number): string => {
    if (!Number.isFinite(value)) return "0";
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  const moduleGradeText = (() => {
    if (activeModuleResult) {
      const score = formatScoreValue(activeModuleResult.score);
      if (hasCorrectnessGrade) {
        return `${expectedAnswerCount}/${answers.length} · ${score}%`;
      }
      return `${score}%`;
    }

    if (hasCorrectnessGrade && answers.length > 0) {
      const fallbackScore = (expectedAnswerCount / answers.length) * 100;
      return `${expectedAnswerCount}/${answers.length} · ${formatScoreValue(fallbackScore)}%`;
    }

    return null;
  })();

  const resolveAudioMime = (format?: string | null): string => {
    if (!format) return "audio/webm";
    const trimmed = format.trim().toLowerCase();
    return trimmed.includes("/") ? trimmed : `audio/${trimmed}`;
  };

  const resolveAudioSrc = (info: ContextGroup["contextInfo"]): string => {
    if (info.audioBase64) {
      return `data:${resolveAudioMime(info.mimeType)};base64,${info.audioBase64}`;
    }
    if (info.mediaUrl) {
      if (
        info.mediaUrl.startsWith("http") ||
        info.mediaUrl.startsWith("data:")
      ) {
        return info.mediaUrl;
      }
      if (
        info.mediaUrl.startsWith("/media/") ||
        info.mediaUrl.startsWith("media/")
      ) {
        const clean = info.mediaUrl.startsWith("/")
          ? info.mediaUrl
          : `/${info.mediaUrl}`;
        return clean;
      }
      return `/media/${info.mediaUrl.startsWith("/") ? info.mediaUrl.slice(1) : info.mediaUrl}`;
    }
    return "";
  };

  const resolveImageSrc = (info: ContextGroup["contextInfo"]): string => {
    if (info.imageBase64) {
      return `data:${info.mimeType || "image/png"};base64,${info.imageBase64}`;
    }
    if (info.mediaUrl) {
      if (
        info.mediaUrl.startsWith("http") ||
        info.mediaUrl.startsWith("data:")
      ) {
        return info.mediaUrl;
      }
      if (
        info.mediaUrl.startsWith("/media/") ||
        info.mediaUrl.startsWith("media/")
      ) {
        const clean = info.mediaUrl.startsWith("/")
          ? info.mediaUrl
          : `/${info.mediaUrl}`;
        return clean;
      }
      return `/media/${info.mediaUrl.startsWith("/") ? info.mediaUrl.slice(1) : info.mediaUrl}`;
    }
    return "";
  };

  const resolveAnswerAudioSrc = (answer: ModuleAnswer): string => {
    if (answer.answer_audio_base64) {
      return `data:${resolveAudioMime(answer.audio_format)};base64,${answer.answer_audio_base64}`;
    }
    if (answer.audio_url && !isAudioDeletedAfterRetention(answer.audio_url)) {
      const url = answer.audio_url;
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      if (url.startsWith("/media/") || url.startsWith("media/")) {
        return url.startsWith("/") ? url : `/${url}`;
      }
      return `/media/${url.startsWith("/") ? url.slice(1) : url}`;
    }
    return "";
  };

  const renderContextSection = (contextInfo: ContextGroup["contextInfo"]) => {
    const hasAudioUrl =
      Boolean(contextInfo.mediaUrl) &&
      (contextInfo.type === "audio" ||
        contextInfo.type === "speaking" ||
        contextInfo.mimeType?.startsWith("audio/") ||
        /\.(mp3|wav|ogg|webm|m4a|flac)$/i.test(contextInfo.mediaUrl || ""));

    const hasImageUrl =
      Boolean(contextInfo.mediaUrl) &&
      (contextInfo.type === "image" ||
        contextInfo.mimeType?.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(contextInfo.mediaUrl || ""));

    const hasAudioContext =
      (contextInfo.type === "audio" || contextInfo.type === "speaking") &&
      (Boolean(contextInfo.audioBase64) || hasAudioUrl);
    const hasImageContext =
      contextInfo.type === "image" &&
      (Boolean(contextInfo.imageBase64) || hasImageUrl);
    const hasTextContext = Boolean(contextInfo.content);

    if (!hasAudioContext && !hasImageContext && !hasTextContext) {
      if (
        !contextInfo.transcript ||
        contextInfo.transcript.trim().length === 0
      ) {
        return null;
      }
    }

    return (
      <div className="space-y-3 p-4">
        {hasAudioContext && (
          <ResultAudioPlayer
            src={resolveAudioSrc(contextInfo)}
            ariaLabel={
              contextInfo.name
                ? t("simulation.results.reviewDialog.namedAudio", {
                    name: contextInfo.name,
                    defaultValue: `${contextInfo.name} audio`,
                  })
                : t("simulation.results.reviewDialog.listeningContextAudio", {
                    defaultValue: "Listening context audio",
                  })
            }
            errorMessage={t(
              "simulation.results.reviewDialog.listeningAudioError",
              {
                defaultValue: "Unable to play this listening audio.",
              },
            )}
          />
        )}

        {hasImageContext && (
          <img
            src={resolveImageSrc(contextInfo)}
            alt={t("simulation.results.reviewDialog.contextAlt", {
              defaultValue: "Context",
            })}
            loading="lazy"
            className="w-full rounded-md border border-border shadow-sm"
          />
        )}

        {hasTextContext && (
          <div className="font-ui rounded-md border border-border bg-background/50 p-4 text-sm leading-relaxed whitespace-pre-wrap">
            {contextInfo.content}
          </div>
        )}

        {contextInfo.transcript && contextInfo.transcript.trim().length > 0 && (
          <div className="space-y-1.5 border-t border-primary/10 pt-2">
            <Label className="font-ui text-xs font-medium text-primary uppercase tracking-wider">
              {t("simulation.results.reviewDialog.transcript", {
                defaultValue: "Transcript",
              })}
            </Label>
            <div className="font-ui rounded bg-secondary/5 p-3 text-sm italic text-muted-foreground whitespace-pre-wrap">
              {contextInfo.transcript}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = (
    answer: ModuleAnswer,
    refCallback: (el: HTMLDivElement | null) => void,
  ) => {
    const expectedAnswers = parseExpectedAnswers(answer.expected_answer);
    const isCorrect = answer.is_correct === true;
    const questionNumber = questionNumberById.get(answer.id);
    const audioWasDeleted = isAudioDeletedAfterRetention(answer.audio_url);
    const hasTextAnswer = Boolean(
      answer.answer_text && answer.answer_text.trim().length > 0,
    );
    const normalizedModuleName = moduleName.toLowerCase();
    const hasPlayableStudentAudio =
      Boolean(answer.answer_audio_base64) ||
      (Boolean(answer.audio_url) && !audioWasDeleted);
    const hasStudentAudio = hasPlayableStudentAudio || audioWasDeleted;
    const isUnanswered = !hasSubmittedAnswer(answer);

    const correctedVersion = getCorrectedVersion(answer);
    const feedbackSummary = normalizeAnswerText(getFeedbackSummary(answer));
    const breakdown = normalizeAnalysisBreakdown(answer);
    const taskFulfillmentCriterion = breakdown?.criteria?.task_fulfillment;
    let taskFulfillmentText = normalizeAnswerText(
      getFeedbackText(answer, "task_fulfillment"),
    );
    if (!taskFulfillmentText && taskFulfillmentCriterion) {
      taskFulfillmentText = getCriterionDisplayFeedback(
        answer,
        "task_fulfillment",
        taskFulfillmentCriterion,
      );
    }
    const pronunciationText = normalizeAnswerText(
      getFeedbackText(answer, "pronunciation"),
    );
    const grammarText = normalizeAnswerText(getFeedbackText(answer, "grammar"));
    const spellingText = normalizeAnswerText(
      getFeedbackText(answer, "spelling"),
    );
    const structureText = normalizeAnswerText(
      getFeedbackText(answer, "structure"),
    );
    const fluencyText = normalizeAnswerText(getFeedbackText(answer, "fluency"));
    const vocabularyText = normalizeAnswerText(
      getFeedbackText(answer, "vocabulary"),
    );
    const registerText = normalizeAnswerText(
      getFeedbackText(answer, "register"),
    );
    const styleText = normalizeAnswerText(getFeedbackText(answer, "style"));
    const interactionText = normalizeAnswerText(
      getFeedbackText(answer, "interaction"),
    );
    const wordTimestamps = getWordTimestamps(answer);
    const supportsAnswerAnnotations =
      normalizedModuleName === "writing" || normalizedModuleName === "speaking";
    const answerAnnotationModel = supportsAnswerAnnotations
      ? buildAnswerAnnotationModel(answer, expectedAnswers, {
          includeFillInBlank: normalizedModuleName === "writing",
        })
      : null;
    const hasTimestampTranscript = wordTimestamps.length > 0;
    const hasReviewableAnnotatedAnswer = Boolean(
      answerAnnotationModel &&
        (answerAnnotationModel.hasInlineAnnotations ||
          (answerAnnotationModel.kind === "fill-in-blank" &&
            answerAnnotationModel.blanks.length > 0)),
    );
    const showAnswerComparisonTabs = supportsAnswerAnnotations;
    const showCorrectedAnswerCompare =
      showAnswerComparisonTabs && correctedVersion.length > 0;
    const showCorrectedVersionInMoreInfo =
      correctedVersion.length > 0 && !showAnswerComparisonTabs;
    const spellingCorrectionPairs = getSpellingCorrectionPairs(answer);
    const getFeedbackReferences = (
      feedbackText: string,
      category: ReviewFeedbackCategory,
    ) =>
      buildFeedbackAnnotationReferences({
        answer,
        model: answerAnnotationModel,
        feedbackText,
        category,
        t,
        spellingCorrectionPairs,
      });
    const pronunciationReferences = getFeedbackReferences(
      pronunciationText,
      "pronunciation",
    );
    const spellingReferences = getFeedbackReferences(spellingText, "spelling");
    const fluencyReferenceText = [fluencyText, ...getFluencyIssueTexts(answer)]
      .filter(Boolean)
      .join("\n");
    const fluencyReferences = getFeedbackReferences(
      fluencyReferenceText,
      "fluency",
    );
    const spellingReferenceIds = new Set(
      spellingReferences.map((reference) => reference.id),
    );
    const grammarReferences = buildFeedbackAnnotationReferences({
      answer,
      model: answerAnnotationModel,
      feedbackText: grammarText,
      category: "grammar",
      t,
      spellingCorrectionPairs,
      excludedReferenceIds: spellingReferenceIds,
    });
    const structureReferences = getFeedbackReferences(
      structureText,
      "structure",
    );
    const taskFulfillmentReferences = getFeedbackReferences(
      taskFulfillmentText,
      "task_fulfillment",
    );

    // An AI-feedback block exists when any AI-produced field is present
    // (explanation for auto-scored modules; rich feedback for writing/speaking).
    const criterionFeedbackSections: CriterionFeedbackSectionConfig[] = [
      {
        key: "task_fulfillment",
        text: taskFulfillmentText,
        labelKey: "task_fulfillment",
        defaultLabel: "Task fulfillment",
        ...getAnalysisSectionTheme("task_fulfillment"),
        icon: <ListChecks className="h-3 w-3" aria-hidden />,
        referenceCategory: "task_fulfillment",
      },
      {
        key: "pronunciation",
        text: pronunciationText,
        labelKey: "pronunciation",
        defaultLabel: "Pronunciation",
        ...getAnalysisSectionTheme("pronunciation"),
        icon: <Mic className="h-3 w-3" aria-hidden />,
        referenceCategory: "pronunciation",
      },
      {
        key: "grammar",
        text: grammarText,
        labelKey: "grammar",
        defaultLabel: "Grammar",
        ...getAnalysisSectionTheme("grammar"),
        icon: <Languages className="h-3 w-3" aria-hidden />,
        referenceCategory: "grammar",
      },
      {
        key: "structure",
        text: structureText,
        labelKey: "structure",
        defaultLabel: "Structure",
        ...getAnalysisSectionTheme("structure"),
        icon: <AlignLeft className="h-3 w-3" aria-hidden />,
        referenceCategory: "structure",
      },
      {
        key: "spelling",
        text: spellingText,
        labelKey: "spelling",
        defaultLabel: "Spelling",
        ...getAnalysisSectionTheme("spelling"),
        icon: <Type className="h-3 w-3" aria-hidden />,
        referenceCategory: "spelling",
      },
      {
        key: "fluency",
        text: fluencyText,
        labelKey: "fluency",
        defaultLabel: "Fluency",
        ...getAnalysisSectionTheme("fluency"),
        icon: <Waves className="h-3 w-3" aria-hidden />,
        referenceCategory: "fluency",
      },
      {
        key: "vocabulary",
        text: vocabularyText,
        labelKey: "vocabulary",
        defaultLabel: "Vocabulary",
        ...getAnalysisSectionTheme("vocabulary"),
        icon: <Book className="h-3 w-3" aria-hidden />,
      },
      {
        key: "register",
        text: registerText,
        labelKey: "register",
        defaultLabel: "Register",
        ...getAnalysisSectionTheme("register"),
        icon: <MessageSquare className="h-3 w-3" aria-hidden />,
      },
      {
        key: "style",
        text: styleText,
        labelKey: "style",
        defaultLabel: "Style",
        ...getAnalysisSectionTheme("style"),
        icon: <Feather className="h-3 w-3" aria-hidden />,
      },
      {
        key: "interaction",
        text: interactionText,
        labelKey: "interaction",
        defaultLabel: "Interaction",
        ...getAnalysisSectionTheme("interaction"),
        icon: <MessageCircle className="h-3 w-3" aria-hidden />,
      },
    ];

    // Weight is the single source of truth: when the answer uses a weighted
    // rubric, only criteria that carry weight for this task type render.
    // Criteria with no weight (e.g. structure for a dialogue/introduction)
    // must not show even if the model returned stray feedback for them. For
    // answers with no rubric weights at all (auto-scored/legacy), fall back
    // to the text-based rule so nothing real disappears.
    const answerUsesWeightedRubric = hasWeightedRubric(answer);
    const visibleCriterionSections = criterionFeedbackSections.filter(
      (section) => {
        const meta = getCriterionMeta(answer, section.key);
        if (answerUsesWeightedRubric) return meta.weight !== null;
        return section.text.length > 0 || meta.weight !== null;
      },
    );

    const hasAiFeedback =
      Boolean(answer.explanation) ||
      visibleCriterionSections.length > 0 ||
      correctedVersion.length > 0;

    const hasAnalysisContent =
      visibleCriterionSections.length > 0 ||
      correctedVersion.length > 0 ||
      feedbackSummary.length > 0 ||
      (!isCorrect &&
        (answer.diagnosis_status === "pending" ||
          (Array.isArray(answer.diagnostic_feedback) &&
            answer.diagnostic_feedback.length > 0)));

    const explanationTheme = getAnalysisSectionTheme("explanation");
    const diagnosticTheme = getAnalysisSectionTheme("diagnostic");

    return (
      <div
        key={answer.id}
        id={`question-${answer.id}`}
        ref={refCallback}
        className="scroll-mt-28"
      >
        <Card className="border-border shadow-sm transition-colors hover:border-primary/30">
          <CardContent className="space-y-4 p-4 sm:p-5">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
              <div className="min-w-0 space-y-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  {typeof questionNumber === "number" && (
                    <Badge className="font-ui h-5 px-1.5 text-[9px] font-bold uppercase tracking-tighter sm:text-[10px]">
                      {t("simulation.results.reviewDialog.questionNumber", {
                        number: questionNumber,
                        defaultValue: `Q${questionNumber}`,
                      })}
                    </Badge>
                  )}
                  <Badge
                    variant="outline"
                    className="font-ui h-5 px-1.5 text-[9px] uppercase tracking-tighter sm:text-[10px]"
                  >
                    {t("simulation.results.reviewDialog.section", {
                      number: answer.section ?? "-",
                      defaultValue: `Section ${answer.section ?? "-"}`,
                    })}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="font-ui h-5 px-1.5 text-[9px] uppercase tracking-tighter sm:text-[10px]"
                  >
                    {formatQuestionTypeLabel(answer.question_type)}
                  </Badge>
                  {typeof answer.points === "number" && (
                    <Badge
                      variant="outline"
                      className="font-ui h-5 px-1.5 text-[9px] uppercase tracking-tighter sm:text-[10px]"
                    >
                      {t("simulation.results.reviewDialog.points", {
                        points: answer.points,
                        defaultValue: `${answer.points} pts`,
                      })}
                    </Badge>
                  )}
                </div>
                <h2 className="font-ui text-sm font-bold leading-snug text-foreground sm:text-base">
                  {answer.question_text}
                </h2>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {isUnanswered ? (
                  <Badge
                    variant="outline"
                    className="font-ui h-5 w-fit border-amber-300 bg-amber-50 px-2 text-[9px] uppercase tracking-widest text-amber-700 sm:text-[10px]"
                  >
                    {notAnsweredLabel}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-ui h-5 w-fit px-2 text-[9px] uppercase tracking-widest sm:text-[10px]",
                      isCorrect
                        ? "border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                        : "border-red-300 bg-red-100 text-red-700 hover:bg-red-100",
                    )}
                  >
                    {isCorrect ? correctLabel : incorrectLabel}
                  </Badge>
                )}
              </div>
            </div>

            {answer.options && answer.options.length > 0 && (
              <div className="space-y-2">
                <Label className="font-ui text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  {t("simulation.results.reviewDialog.options", {
                    defaultValue: "Options",
                  })}
                </Label>
                <ul className="font-ui list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                  {answer.options.map((option, idx) => (
                    <li key={`${answer.id}-option-${idx}`}>{option}</li>
                  ))}
                </ul>
              </div>
            )}

            {(() => {
              const expectedAnswerColumn = (
                <div className="flex flex-col space-y-2">
                  <Label className="font-ui text-xs font-medium text-success uppercase tracking-widest">
                    {t("simulation.results.reviewDialog.expectedAnswer", {
                      defaultValue: "Expected Answer",
                    })}
                  </Label>
                  {expectedAnswers.length > 0 ? (
                    <div className="font-ui flex-1 rounded-md border border-success/20 bg-success/5 p-3 text-sm text-foreground/90 whitespace-pre-wrap selection:bg-success/20">
                      {expectedAnswers.map((expected, idx) => (
                        <div
                          key={`${answer.id}-correct-${idx}`}
                          className="flex items-start gap-2"
                        >
                          {expectedAnswers.length > 1 && (
                            <span className="mt-1 opacity-50">•</span>
                          )}
                          <span>{expected}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="font-ui flex-1 rounded-md bg-muted/30 p-3 text-center text-sm italic text-muted-foreground">
                      {t("simulation.results.reviewDialog.noExpectedAnswer", {
                        defaultValue: "No specific expected answer available.",
                      })}
                    </div>
                  )}
                </div>
              );

              const yourAnswerBody =
                hasStudentAudio ||
                hasTextAnswer ||
                hasReviewableAnnotatedAnswer ? (
                  <div className="font-ui flex-1 space-y-3 rounded-md border border-primary/15 bg-accent/5 p-3 text-sm text-foreground/90 whitespace-pre-wrap">
                    {hasPlayableStudentAudio && (
                      <StudentAnswerAudioPlayer
                        ref={getAudioRef(answer.id)}
                        src={resolveAnswerAudioSrc(answer)}
                        fallbackDurationSeconds={
                          answer.audio_duration_seconds ?? null
                        }
                      />
                    )}

                    {audioWasDeleted && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/50 p-3 text-amber-900">
                        <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="text-xs leading-relaxed">
                          {t("simulation.results.audio.deletedForPrivacy", {
                            defaultValue:
                              "This recording is no longer available. Audio recordings are automatically deleted after 7 days for privacy.",
                          })}
                        </div>
                      </div>
                    )}

                    {hasReviewableAnnotatedAnswer &&
                    answerAnnotationModel &&
                    !hasTimestampTranscript ? (
                      <AnnotatedAnswer
                        model={answerAnnotationModel}
                        answerId={answer.id}
                        activeAnnotationId={activeAnnotationId}
                      />
                    ) : hasTextAnswer && answer.answer_text ? (
                      <div>
                        {hasStudentAudio && (
                          <div className="font-ui mb-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                            {t("simulation.results.reviewDialog.transcript", {
                              defaultValue: "Transcript",
                            })}
                          </div>
                        )}
                        {wordTimestamps.length > 0 ? (
                          <TranscriptWithTimestamps
                            words={wordTimestamps}
                            audioRef={getAudioRef(answer.id)}
                            fallbackText={answer.answer_text}
                            answerId={answer.id}
                            activeAnnotationId={activeAnnotationId}
                            textAnnotationSegments={
                              answerAnnotationModel?.kind === "text"
                                ? answerAnnotationModel.segments
                                : []
                            }
                          />
                        ) : (
                          <div className="leading-relaxed">
                            {answer.answer_text}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="font-ui flex-1 rounded-md bg-muted/30 p-3 text-center text-sm italic text-muted-foreground">
                    {t("simulation.results.reviewDialog.noAnswerProvided", {
                      defaultValue: "No answer provided.",
                    })}
                  </div>
                );

              if (showAnswerComparisonTabs) {
                return (
                  <div data-corrected-answer-compare="tabs">
                    <AnswerComparisonTabs
                      answerId={answer.id}
                      correctedVersion={correctedVersion}
                      expectedAnswers={expectedAnswers}
                      yourAnswer={yourAnswerBody}
                      activeAnnotationId={activeAnnotationId}
                    />
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {expectedAnswerColumn}
                  <div className="flex flex-col space-y-2">
                    <Label className="font-ui text-xs font-medium text-primary uppercase tracking-widest">
                      {t("simulation.results.reviewDialog.yourAnswer", {
                        defaultValue: "Your Answer",
                      })}
                    </Label>
                    {yourAnswerBody}
                  </div>
                </div>
              );
            })()}

            {answer.explanation && (
              <div className="space-y-2 border-t border-border/50 pt-2">
                <Label
                  className={cn(
                    "font-ui text-xs font-medium uppercase tracking-widest",
                    explanationTheme.labelClassName,
                  )}
                >
                  {t("simulation.results.reviewDialog.explanation", {
                    defaultValue: "Explanation",
                  })}
                </Label>
                <div
                  className={cn(
                    "font-ui rounded-md border p-3 text-sm leading-relaxed text-foreground/80",
                    explanationTheme.boxClassName,
                  )}
                >
                  {answer.explanation}
                </div>
              </div>
            )}

            {hasAnalysisContent && (
              <div className="relative my-2 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="font-ui shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">
                  {t("simulation.results.reviewDialog.analysisLabel", {
                    defaultValue: "Analysis",
                  })}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}

            {!isCorrect &&
              answer.diagnosis_status === "pending" &&
              (!answer.diagnostic_feedback ||
                answer.diagnostic_feedback.length === 0) && (
                <div className="space-y-2">
                  <Label
                    className={cn(
                      "font-ui text-xs font-medium uppercase tracking-widest",
                      diagnosticTheme.labelClassName,
                    )}
                  >
                    {t("diagnostic.review.whyWrong", {
                      defaultValue: "Why this was tempting",
                    })}
                  </Label>
                  <div
                    className={cn(
                      "font-ui flex items-center gap-2 rounded-md border p-3 text-sm text-foreground/80",
                      diagnosticTheme.boxClassName,
                    )}
                  >
                    <Loader2
                      className="h-4 w-4 shrink-0 animate-spin text-rose-600"
                      aria-hidden
                    />
                    {gradingPendingLabel}
                  </div>
                </div>
              )}

            {!isCorrect &&
              Array.isArray(answer.diagnostic_feedback) &&
              answer.diagnostic_feedback.length > 0 && (
                <div className="space-y-2">
                  <Label
                    className={cn(
                      "font-ui text-xs font-medium uppercase tracking-widest",
                      diagnosticTheme.labelClassName,
                    )}
                  >
                    {t("diagnostic.review.whyWrong", {
                      defaultValue: "Why this was tempting",
                    })}
                  </Label>
                  <div className="space-y-2">
                    {answer.diagnostic_feedback.map((fb) => (
                      <div
                        key={fb.tag}
                        className={cn(
                          "font-ui rounded-md border p-3 text-sm leading-relaxed text-foreground/80",
                          diagnosticTheme.boxClassName,
                        )}
                      >
                        <p>{fb.explain}</p>
                        {fb.resource?.title ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t("diagnostic.profile.recommended", {
                              defaultValue: "Recommended",
                            })}
                            :{" "}
                            <span className="font-medium text-foreground">
                              {fb.resource.title}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {visibleCriterionSections.length > 0 &&
              (() => {
                const scorecardCategories: ScorecardCategory[] =
                  visibleCriterionSections.map((section) => {
                    const meta = getCriterionMeta(answer, section.key);
                    const hasMissingFeedback = section.text.length === 0;
                    const displayText = hasMissingFeedback
                      ? t(
                          "simulation.results.reviewDialog.missingCriterionFeedback",
                          {
                            defaultValue:
                              "Required feedback for this weighted criterion is missing.",
                          },
                        )
                      : section.text;
                    const references = resolveSectionReferences(
                      section.referenceCategory,
                      pronunciationReferences,
                      fluencyReferences,
                      grammarReferences,
                      spellingReferences,
                      structureReferences,
                      taskFulfillmentReferences,
                    );
                    return {
                      key: section.key,
                      label: t(
                        `simulation.results.reviewDialog.${section.labelKey}`,
                        {
                          defaultValue: section.defaultLabel,
                        },
                      ),
                      icon: section.icon,
                      score: meta.score,
                      weight: meta.weight,
                      body: (
                        <div
                          className={cn(
                            "font-ui text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap",
                          )}
                        >
                          <div
                            className={cn(
                              hasMissingFeedback &&
                                "italic text-muted-foreground",
                            )}
                          >
                            {displayText}
                          </div>
                          {!hasMissingFeedback &&
                            references.length > 0 &&
                            renderFeedbackReferences(references)}
                        </div>
                      ),
                    } satisfies ScorecardCategory;
                  });
                return (
                  <RubricScorecard
                    categories={scorecardCategories}
                    finalScore={breakdown?.final_score ?? null}
                  />
                );
              })()}

            {feedbackSummary.length > 0 &&
              visibleCriterionSections.length === 0 && (
                <div className="font-ui rounded-md border border-border bg-muted/20 p-3 text-sm italic leading-relaxed text-muted-foreground">
                  {feedbackSummary}
                </div>
              )}

            {showCorrectedVersionInMoreInfo && (
              <CorrectedAnswerPanel correctedVersion={correctedVersion} />
            )}

            <div className="space-y-2 pt-1">
              {isLearnerRatingsMode ? (
                <>
                  <ItemRatingReadOnly
                    targetType="question"
                    module={ratingModule}
                    rating={ratingsByKey.get(`question:${answer.id}`) ?? null}
                  />
                  {hasAiFeedback ? (
                    <ItemRatingReadOnly
                      targetType="ai_feedback"
                      module={ratingModule}
                      rating={
                        ratingsByKey.get(`ai_feedback:${answer.id}`) ?? null
                      }
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <RatingRow
                    targetType="question"
                    targetId={answer.id}
                    module={ratingModule}
                    initialRating={
                      ratingsByKey.get(`question:${answer.id}`) ?? null
                    }
                  />
                  {hasAiFeedback ? (
                    <RatingRow
                      targetType="ai_feedback"
                      targetId={answer.id}
                      module={ratingModule}
                      initialRating={
                        ratingsByKey.get(`ai_feedback:${answer.id}`) ?? null
                      }
                    />
                  ) : null}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const showModuleTabs =
    sessionModules.length > 0 && typeof onSessionModuleChange === "function";

  const handleModuleTabChange = (value: string) => {
    if (value === moduleName) return;
    onSessionModuleChange?.(value);
  };

  const renderQuestionGroups = (
    contextGroups: readonly ContextGroup[],
    standaloneAnswers: readonly ModuleAnswer[],
  ) => (
    <>
      {contextGroups.map((group, groupIdx) => {
        const contextSection = renderContextSection(group.contextInfo);
        return (
          <div
            key={group.contextKey || `group-${groupIdx}`}
            className={cn(
              contextSection
                ? "grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(18rem,0.9fr)_minmax(0,1.4fr)] xl:grid-cols-[minmax(20rem,0.85fr)_minmax(0,1.65fr)]"
                : "space-y-4",
            )}
          >
            {contextSection && (
              <div
                className={cn(
                  "overflow-y-auto rounded-lg border-2 border-primary/20 bg-accent/5",
                  isPageLayout
                    ? "lg:sticky lg:top-4 lg:max-h-[calc(100vh-18rem)]"
                    : "max-h-[55vh] lg:sticky lg:top-4",
                )}
              >
                {contextSection}
              </div>
            )}
            <div className="min-w-0 space-y-4">
              {group.answers.map((answer) =>
                renderQuestion(answer, (el) => {
                  if (el) questionRefs.current.set(answer.id, el);
                  else questionRefs.current.delete(answer.id);
                }),
              )}
            </div>
          </div>
        );
      })}

      {standaloneAnswers.length > 0 && (
        <div className="space-y-4">
          {standaloneAnswers.map((answer) =>
            renderQuestion(answer, (el) => {
              if (el) questionRefs.current.set(answer.id, el);
              else questionRefs.current.delete(answer.id);
            }),
          )}
        </div>
      )}
    </>
  );

  const body = (
    <div className={cn(isPageLayout ? "p-3 sm:p-6" : "p-4 sm:p-6")}>
      {isLoading && answers.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
          <span className="font-ui animate-pulse text-sm uppercase tracking-widest">
            {t("simulation.results.reviewDialog.loadingAnswers", {
              defaultValue: "Loading answers...",
            })}
          </span>
        </div>
      ) : (
        <>
          {answers.length > 0 && (
            <div className="pb-10">
              {useInterleavedSectionReview ? (
                sectionReviewBlocks.map((block, blockIdx) => (
                  <div key={block.section}>
                    <div
                      className={cn(
                        "flex items-center gap-3 py-5",
                        blockIdx > 0 && "mt-4",
                      )}
                    >
                      <div className="h-px flex-1 bg-border/60" />
                      <span className="font-ui shrink-0 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        {t("simulation.results.reviewDialog.sectionLabel", {
                          number: block.section,
                          defaultValue: `Section ${block.section}`,
                        })}
                      </span>
                      <div className="h-px flex-1 bg-border/60" />
                    </div>

                    <div className="space-y-6">
                      {(block.contextGroups.length > 0 ||
                        block.standaloneAnswers.length > 0) && (
                        <div className="space-y-4">
                          {renderQuestionGroups(
                            block.contextGroups,
                            block.standaloneAnswers,
                          )}
                        </div>
                      )}
                      {block.sectionFeedback &&
                        sectionFeedbackRowHasContent(block.sectionFeedback) && (
                          <div className="space-y-3 border-t border-border/60 pt-6">
                            <Label className="font-ui block text-center text-xs font-bold uppercase tracking-[0.2em] text-primary">
                              {t(
                                "simulation.results.reviewDialog.moreInformation",
                                {
                                  defaultValue: "More Information",
                                },
                              )}
                            </Label>
                            <SectionFeedbackCard row={block.sectionFeedback} />
                          </div>
                        )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="space-y-4 pt-2">
                  {renderQuestionGroups(
                    groupedAnswers.contextGroups,
                    groupedAnswers.standaloneAnswers,
                  )}
                </div>
              )}
              <div className="mt-8 border-t border-border/40 pt-4">
                <p className="font-ui text-center text-[11px] leading-5 text-muted-foreground/70 xl:whitespace-nowrap">
                  {t("simulation.results.reviewDialog.aiDisclosure", {
                    defaultValue:
                      "AI-assisted feedback is guided by task instructions, expected answers, and exam-style scoring rules, but may occasionally be imperfect.",
                  })}
                </p>
              </div>
            </div>
          )}
          {!isLoading && answers.length === 0 && (
            <div className="flex flex-col items-center justify-center space-y-2 py-20 text-muted-foreground opacity-50">
              <div className="text-4xl">📭</div>
              <p className="font-ui text-sm">
                {t("simulation.results.reviewDialog.noAnswersRecorded", {
                  defaultValue: "No answers recorded for this module.",
                })}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <section
      role="region"
      aria-label={reviewRegionAriaLabel}
      className={cn(
        "min-w-0 overflow-hidden bg-background",
        isPageLayout
          ? "flex h-[calc(100dvh-4.125rem)] min-h-0 flex-col border-y border-border shadow-sm sm:h-[calc(100dvh-7rem)] sm:rounded-xl sm:border lg:h-[calc(100vh-8rem)] lg:min-h-[34rem]"
          : "flex min-h-0 flex-1 flex-col",
        className,
      )}
    >
      {showModuleTabs && (
        <div className="border-b border-border/70 bg-muted/30 px-3 py-1.5 sm:px-6 sm:py-2">
          <div className="mb-1.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="font-ui text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t("simulation.results.reviewDialog.sectionsLabel", {
                defaultValue: "Exam sections",
              })}
            </p>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              {headerAction ? (
                <div className="shrink-0">{headerAction}</div>
              ) : null}
            </div>
          </div>
          <Tabs
            value={moduleName}
            onValueChange={handleModuleTabChange}
            className="w-full"
            aria-label={t("simulation.results.reviewDialog.ariaLabel", {
              defaultValue: "Switch active exam section",
            })}
          >
            <TabsList
              className={cn(
                "flex h-auto min-h-9 w-full flex-nowrap gap-0.5 overflow-x-auto rounded-lg border border-border bg-muted p-0.5 shadow-inner",
                "lg:min-h-8 lg:overflow-visible lg:scrollbar-hide",
              )}
            >
              {sessionModules.map((mod) => {
                const Icon = SIMULATION_MODULE_ICONS[mod.module];
                const label = t(`moduleSelection.modules.${mod.module}.label`, {
                  defaultValue: SIMULATION_MODULE_SHORT_LABELS[mod.module],
                });
                const disabled = !isModuleReviewAvailable(mod);
                return (
                  <TabsTrigger
                    key={mod.module}
                    value={mod.module}
                    disabled={disabled}
                    title={disabled ? gradingPendingLabel : undefined}
                    className={cn(
                      "group font-ui flex min-h-11 min-w-[4.5rem] flex-1 shrink-0 snap-start items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold lg:min-h-7 lg:min-w-[6rem]",
                      "text-muted-foreground transition-colors",
                      "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
                      "data-[state=inactive]:hover:bg-background/65 data-[state=inactive]:hover:text-foreground",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted",
                    )}
                  >
                    {Icon ? (
                      <Icon
                        className="h-3.5 w-3.5 shrink-0 opacity-70 transition-opacity group-data-[state=active]:opacity-100"
                        aria-hidden
                      />
                    ) : null}
                    <span className="font-ui truncate">{label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>
      )}

      {!showModuleTabs && headerAction ? (
        <div className="flex justify-end border-b bg-background/80 px-4 py-2 backdrop-blur-md sm:px-6">
          {headerAction}
        </div>
      ) : null}

      {answers.length > 0 && !isLoading && (
        <div
          className={cn(
            "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b bg-muted/25 px-3 py-1.5 sm:px-6 sm:py-2",
          )}
        >
          <div className="flex min-w-0 items-center">
            <span className="font-ui mr-1.5 shrink-0 text-xs font-medium text-muted-foreground sm:mr-2">
              {t("simulation.results.reviewDialog.questions", {
                defaultValue: "Questions:",
              })}
            </span>
            <div className="scrollbar-hide flex min-w-0 flex-1 snap-x snap-mandatory items-center overflow-x-auto overscroll-x-contain">
              {questionNavigatorItems.map((item, index) => {
                const prevSection =
                  index > 0
                    ? questionNavigatorItems[index - 1]?.answer.section
                    : undefined;
                const currentSection = item.answer.section;
                const showSectionSeparator =
                  index > 0 &&
                  prevSection !== undefined &&
                  currentSection !== undefined &&
                  prevSection !== currentSection;
                const correctnessLabel = item.isCorrect
                  ? correctLabel
                  : incorrectLabel;
                const answerStatusLabel = item.isUnanswered
                  ? t("simulation.results.reviewDialog.notAnsweredSuffix", {
                      defaultValue: ", not answered",
                    })
                  : "";
                const buttonLabel = t(
                  "simulation.results.reviewDialog.questionAria",
                  {
                    number: item.number,
                    status: correctnessLabel,
                    answerStatus: answerStatusLabel,
                    defaultValue: `Question ${item.number}, ${correctnessLabel}${answerStatusLabel}`,
                  },
                );
                return (
                  <Fragment key={item.answer.id}>
                    {showSectionSeparator ? (
                      <div
                        className="mx-0.5 h-5 w-px shrink-0 self-center bg-border"
                        aria-hidden
                      />
                    ) : null}
                    {/* Outer element provides a 44×44 px minimum touch target (WCAG 2.5.5)
                                        while keeping the visible circle at h-6 w-6 (24 px). */}
                    <div className="flex min-h-11 min-w-11 snap-start items-center justify-center">
                      <button
                        type="button"
                        onClick={() => scrollToQuestion(item.answer.id)}
                        aria-label={buttonLabel}
                        className={cn(
                          "h-6 w-6 rounded-full text-[11px] font-bold transition-all",
                          "hover:ring-2 hover:ring-primary/50 hover:ring-offset-1",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1",
                          item.isUnanswered
                            ? "border border-amber-300 bg-amber-100 text-amber-800"
                            : item.isCorrect
                              ? "border border-emerald-300 bg-emerald-100 text-emerald-700"
                              : "border border-red-300 bg-red-100 text-red-700",
                        )}
                        title={buttonLabel}
                      >
                        {item.number}
                      </button>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
          {moduleGradeText ? (
            <div className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
              <span className="font-ui hidden text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline">
                {t("simulation.results.reviewDialog.totalGrade", {
                  defaultValue: "Total grade",
                })}
              </span>
              <Badge className="font-ui rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary hover:bg-primary/10">
                {moduleGradeText}
              </Badge>
            </div>
          ) : null}
        </div>
      )}

      <ScrollArea ref={scrollAreaRef} className="min-h-0 flex-1">
        {body}
      </ScrollArea>
    </section>
  );
}
